class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.formId = this.getAttribute('form-id');
    this.originalForm = null;
    this.originalVariantRadios = null;
    this.originalAddToCartButton = null;
    this.stickyAddToCartButton = null;
    this.formBottomPosition = 0;
    this.customSelects = {};

    this.attachShadow({ mode: 'open' });

  }
  
  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.originalForm = document.getElementById(this.formId);
    this.originalVariantRadios = document.querySelector('variant-radios');
    this.originalAddToCartButton = this.originalForm.querySelector('button[type="submit"]');
    this.stickyAddToCartButton = this.querySelector('#add-to-cart-button');

    this.stickyAddToCartButton.addEventListener('click', (e) => {
      const sizeCustomSelect = this.customSelects['size'];
      if (sizeCustomSelect && !sizeCustomSelect.selectedValue) {
        e.preventDefault();
        this.displayErrorMessage('Please select a size to add to your bag.');
      }
    });

    if (!this.originalForm || !this.originalVariantRadios || !this.originalAddToCartButton) {
      console.error('Could not find original product form elements');
      return;
    }

    this.render();
    this.setupCustomSelects();
    this.setupEventListeners();
    this.updateButtonState();
    this.updatePrice();
    this.initializeSelectsFromOriginalForm();


    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.handleCartErrors.bind(this));
  }

  render() {
    const template = this.querySelector('template');
    if (!template) {
      console.error('No template found for sticky add to cart');
      return;
    }

    const clone = template.content.cloneNode(true);
    this.shadowRoot.appendChild(clone);

    template.remove();
  }

  setupCustomSelects() {
    const selects = this.shadowRoot.querySelectorAll('custom-select');
    
    selects.forEach(select => {
      const optionType = select.getAttribute('option-type');
      this.customSelects[optionType] = select;

      select.addEventListener('select-change', this.handleSelectChange.bind(this));
    });
  }

  handleSelectChange(event) {
    const { optionType, value, label } = event.detail;
    this.selectOriginalRadio(optionType, value);

    this.clearErrorMessage();
  }

  selectOriginalRadio(optionType, value) {
    const fieldsets = this.originalVariantRadios.querySelectorAll('fieldset');
    let targetFieldset;
    
    for (const fieldset of fieldsets) {
      const dataOption = fieldset.dataset.productOption;
 
      if (dataOption === optionType) {
        targetFieldset = fieldset;
        break;
      }
    }
    
    if (!targetFieldset) {
      console.error(`Could not find fieldset for option type: ${optionType}`);
      return;
    }
    const radio = targetFieldset.querySelector(`input[type="radio"][value="${value}"]`);
    
    if (!radio) {
      console.error(`Could not find radio input for ${optionType} with value: ${value}`);
      return;
    }

    if (radio.checked) return;
    radio.checked = true;
    this.triggerChangeEvent(radio);
  }

  initializeSelectsFromOriginalForm() {
    const fieldsets = this.originalVariantRadios.querySelectorAll('fieldset');
    
    fieldsets.forEach(fieldset => {
      const optionType = fieldset.dataset.productOption;
      if (!optionType || !this.customSelects[optionType]) return;
      
      const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
      const checkedRadio = Array.from(radioInputs).find(radio => radio.checked);
      
      if (checkedRadio) {
        const value = checkedRadio.value;
        this.customSelects[optionType].setSelection(value);
      }
    });
  }

  setupEventListeners() {
    const formRect = this.originalForm.getBoundingClientRect();
    this.formBottomPosition = formRect.top + formRect.height + window.scrollY;

    this.setupVisibilityObserver();
    this.setupFormObserver();
    this.setupVariantRadioListeners();
  }

  setupVariantRadioListeners() {
    const fieldsets = this.originalVariantRadios.querySelectorAll('fieldset');
    
    fieldsets.forEach(fieldset => {
      const optionType = fieldset.dataset.productOption;
      if (!optionType || !this.customSelects[optionType]) return;

      const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
      radioInputs.forEach(radio => {
        radio.addEventListener('change', () => {
          if (radio.checked) {
            const value = radio.value;
            this.customSelects[optionType].setSelection(value);
          }
        });
      });
    });
  }

  setupVisibilityObserver() {
    this.observer = new IntersectionObserver((entries) => {
      const [entry] = entries;

      const headerHeight = document.querySelector('sticky-header')?.offsetHeight || 0;
      const announcementBarHeight = document.querySelector('announcement-bar')?.offsetHeight || 0;
      const totalOffset = headerHeight + announcementBarHeight;

      if (!entry.isIntersecting && entry.boundingClientRect.bottom <= totalOffset) {
        this.classList.add('visible');
      } else {
        this.classList.remove('visible');
      }
    });
    
    this.observer.observe(this.originalForm);
  }


  setupFormObserver() {
    const config = { attributes: true, childList: true, subtree: true };

    const observer = new MutationObserver(() => {
      this.updateButtonState();
      this.updatePrice();
      this.updateOptionsAvailability();
    });

    observer.observe(this.originalForm, config);
  }

  updateButtonState() {
    if (this.originalAddToCartButton && this.stickyAddToCartButton) {
      const originalButtonText = this.originalAddToCartButton.querySelector('span')?.textContent || 'Add to Cart';
      this.stickyAddToCartButton.querySelector('span').textContent = originalButtonText;
      this.stickyAddToCartButton.disabled = this.originalAddToCartButton.disabled;

      // If the original button is disabled, and also "Sold Out", display "Notify Me" button
      const notifyMeButton = this.querySelector('.bis-trigger-button');
      if (this.originalAddToCartButton.disabled && this.originalAddToCartButton.textContent?.toLocaleLowerCase().includes('sold')) {
        if (notifyMeButton) {
          notifyMeButton.classList.remove('hidden');
        }
      } else {
        if (notifyMeButton) {
          notifyMeButton.classList.add('hidden');
        }
      }
    }
  }

  updatePrice() {
    const priceElements = this.shadowRoot.querySelectorAll('[data-product-price]');
    const originalPriceElement = document.querySelector('.product__price');

    if (priceElements.length && originalPriceElement) {
      priceElements.forEach(priceElement => {
        priceElement.innerHTML = '';
        priceElement.appendChild(originalPriceElement.cloneNode(true));
      });
    }
  }

  updateOptionsAvailability() {
    const fieldsets = this.originalVariantRadios.querySelectorAll('fieldset');
    fieldsets.forEach(fieldset => {
      const optionType = fieldset.dataset.productOption;
      if (!optionType || !this.customSelects[optionType]) return;

      const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
      radioInputs.forEach(radio => {
        const isDisabled = radio.classList.contains('disabled');
        this.customSelects[optionType].setDisability(radio.value, isDisabled);
      });
    });
  }

  triggerChangeEvent(element) {
    const event = new Event('change', { bubbles: true });
    element.dispatchEvent(event);
  }

  displayErrorMessage(message) {
    const errorContainers = this.shadowRoot.querySelectorAll('[data-warning-container]');
    if (errorContainers.length) {
      errorContainers.forEach(container => {
        container.innerHTML = message;
      });
    }
  }

  clearErrorMessage() {
    const errorContainers = this.shadowRoot.querySelectorAll('[data-warning-container]');
    if (errorContainers.length) {
      errorContainers.forEach(container => {
        container.innerHTML = '';
      });
    }
  }

  handleCartErrors(error) {
    if (error.message) {
      this.displayErrorMessage(error.message);
    }
  }
}

customElements.whenDefined('custom-select').then(() => {
  // The StickyAddToCart heavly relies on the custom-select component, so we need to wait for it to be defined before defining the StickyAddToCart component.
  customElements.define('sticky-add-to-cart', StickyAddToCart);
});